'use client';

import { useEffect, useRef, useState } from 'react';
import { urlParameterCodes } from '@/constants/urlParams';
import { AttributeTileDetail } from '@/ui/components/pages/ProductDetail/types/attribute-tile-detail';
import { ColorDetail } from '@/ui/components/pages/ProductDetail/types/ColorDetail';
import { ParsedHardwareProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedHardwareProductVariant';
import { useCurrentVariant } from '@/ui/hooks/productDetail/useCurrentVariant';
import {
  useExternalAreaLink,
  useExternalAreaLinkDismissed,
} from '@/ui/providers/externalAreaLinkProvider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { Accessory } from '@/ui/services/AccessoryService';
import { Smartlock } from '@/ui/services/SmartlockService';
import { useCartStore } from '@/ui/store/cart';
import { getUrlParamValue } from '@/ui/utils/client-url-helpers';
import {
  filterHiddenByCountry,
  getSoleAvailableOption,
  isCountryRestrictedWithMessage,
  isHiddenByCountry,
} from '@/utils/tile-country-visibility';

export const CLASSIC_PDP_MAX_QUANTITY = 10;

export const CLASSIC_PDP_MIN_QUANTITY = 1;

export type ClassicPdpProduct = Smartlock | Accessory;

// Superset of the Smart Lock and accessory variants: the classic layout renders
// the same three selectable dimensions for both shapes.
export interface ClassicPdpVariant extends ParsedHardwareProductVariant {
  cylinder?: AttributeTileDetail;
  wildcard?: AttributeTileDetail;
}

export interface ClassicPdpSelection {
  color?: string;
  cylinder?: string;
  wildcard?: string;
}

export interface ClassicPdpSelectionState {
  currentVariant: ClassicPdpVariant;
  visibleColors: ColorDetail[];
  visibleCylinders: AttributeTileDetail[];
  visibleWildcards: AttributeTileDetail[];
  quantity: number;
  isAvailable: boolean;
  isShippingModalOpen: boolean;
  externalAreaLink: string | null;
  setQuantity: (quantity: number) => void;
  selectVariant: (selection: ClassicPdpSelection) => void;
  addToCart: () => void;
  notifyMe: () => void;
  closeShippingModal: () => void;
  proceedDespiteShippingWarning: () => void;
}

function getWildcards(product: ClassicPdpProduct): AttributeTileDetail[] {
  return 'availableWildcards' in product ? product.availableWildcards : [];
}

// Accessories select across three dimensions, Smart Locks only across two, and
// the `in` check narrows the union so neither call needs a cast.
function resolveVariant(
  product: ClassicPdpProduct,
  selection: ClassicPdpSelection,
): ClassicPdpVariant | undefined {
  if ('availableWildcards' in product) {
    return product.getSelectedVariant(selection);
  }

  return product.getSelectedVariant({
    color: selection.color,
    cylinder: selection.cylinder,
  });
}

/**
 * Owns every stateful concern of the classic purchase panel: variant selection
 * across the color/cylinder/wildcard dimensions, country restrictions,
 * quantity, availability and the add-to-cart / notify-me actions. The panel and
 * the gallery stay presentational and read this one contract.
 */
export function useClassicPdpSelection(product: ClassicPdpProduct): ClassicPdpSelectionState {
  const { country } = useLocalization();
  const { setShowNotifyMe } = useCartStore();
  const externalAreaLink = useExternalAreaLink();
  const { isDismissed, dismiss } = useExternalAreaLinkDismissed();

  const { currentVariant, setSelectedVariant } = useCurrentVariant<ClassicPdpVariant>(
    product,
    true,
  );

  const [quantity, setQuantity] = useState(CLASSIC_PDP_MIN_QUANTITY);
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
  const notifyOosTriggeredRef = useRef(false);

  const availableWildcards = getWildcards(product);

  // Options configured to "hide" for the country are dropped; the ones kept in
  // "show_message" mode stay selectable and surface the unavailability notice.
  const visibleColors = product.hideColorSelection ? [] : product.availableColors;
  const visibleCylinders = filterHiddenByCountry(product.availableCylinders, country);
  const visibleWildcards = filterHiddenByCountry(availableWildcards, country);

  // Every change threads the full current selection through and overrides only
  // the dimension that moved, so switching one picker keeps the others.
  const selectVariant = (selection: ClassicPdpSelection) => {
    const selectedVariant = resolveVariant(product, {
      color: currentVariant?.color?.name,
      cylinder: currentVariant?.cylinder?.type,
      wildcard: currentVariant?.wildcard?.type,
      ...selection,
    });

    if (selectedVariant && selectedVariant.sku !== currentVariant?.sku) {
      setSelectedVariant(selectedVariant);
    }
  };

  const soleCylinder = getSoleAvailableOption(product.availableCylinders, country);
  const soleWildcard = getSoleAvailableOption(availableWildcards, country);

  // Snap to the only fully available option per dimension, but only while the
  // current selection is unset or itself hidden, so a deliberately picked
  // "show_message" option is never overridden.
  useEffect(() => {
    if (!currentVariant) {
      return;
    }

    const { cylinder, wildcard } = currentVariant;
    const patch: ClassicPdpSelection = {};

    if (
      soleCylinder &&
      (!cylinder || isHiddenByCountry(cylinder, country)) &&
      cylinder?.type !== soleCylinder.type
    ) {
      patch.cylinder = soleCylinder.type;
    }

    if (
      soleWildcard &&
      (!wildcard || isHiddenByCountry(wildcard, country)) &&
      wildcard?.type !== soleWildcard.type
    ) {
      patch.wildcard = soleWildcard.type;
    }

    if (!patch.cylinder && !patch.wildcard) {
      return;
    }

    selectVariant(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    soleCylinder?.type,
    soleWildcard?.type,
    currentVariant?.cylinder?.type,
    currentVariant?.wildcard?.type,
    country,
  ]);

  // A different SKU is a different purchase, so the amount starts over.
  useEffect(() => {
    setQuantity(CLASSIC_PDP_MIN_QUANTITY);
  }, [currentVariant?.sku]);

  // /direct-purchase sends visitors back with ?notifyOos=true for out-of-stock
  // items; open the notify-me flow once the variant has settled.
  useEffect(() => {
    if (notifyOosTriggeredRef.current || !currentVariant) {
      return;
    }

    if (getUrlParamValue(urlParameterCodes.productDetail.notifyOutOfStock) !== 'true') {
      return;
    }

    notifyOosTriggeredRef.current = true;
    setShowNotifyMe(true, currentVariant, null);
  }, [currentVariant, setShowNotifyMe]);

  const isSelectionRegionRestricted = [currentVariant?.cylinder, currentVariant?.wildcard].some(
    (option) => !!option && isCountryRestrictedWithMessage(option, country),
  );
  const isInStock = !!currentVariant && product.isVariantInStock(currentVariant);
  const isAvailable = (isInStock || !!externalAreaLink) && !isSelectionRegionRestricted;

  const effectiveExternalAreaLink = isDismissed ? null : externalAreaLink;

  const addToCart = () => {
    // An external area link means this area is served by another shop, so the
    // visitor confirms in the modal before the item lands in this cart.
    if (effectiveExternalAreaLink) {
      setIsShippingModalOpen(true);
      return;
    }

    if (!currentVariant?.sku || !currentVariant.priceVariant) {
      return;
    }

    // The classic layout sells its add-ons in the upsell grid below the panel,
    // so the cart upselling chooser would offer the same items a second time.
    product.addToCart(currentVariant, { quantity, skipCartUpselling: true });
  };

  const notifyMe = () => {
    if (!currentVariant) {
      return;
    }

    setShowNotifyMe(true, currentVariant, null);
  };

  const proceedDespiteShippingWarning = () => {
    setIsShippingModalOpen(false);
    dismiss();
  };

  const closeShippingModal = () => setIsShippingModalOpen(false);

  return {
    currentVariant,
    visibleColors,
    visibleCylinders,
    visibleWildcards,
    quantity,
    isAvailable,
    isShippingModalOpen,
    externalAreaLink,
    setQuantity,
    selectVariant,
    addToCart,
    notifyMe,
    closeShippingModal,
    proceedDespiteShippingWarning,
  };
}
